import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft, Building2, CalendarDays, TrendingUp,
  Play, User, EyeOff,
} from "lucide-react";
import { HeartIcon, StudentIcon } from "../lib/icons";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { CreateCampaign } from "../components/CreateCampaign";
import { DonateDialog } from "../components/DonateDialog";
import { FadeUp, CountUp } from "../components/Reveal";
import { PageAccent } from "../components/PageAccent";
import { CampaignShareBar } from "../components/CampaignShareBar";
import { PortraitFallback } from "../components/PortraitFallback";
import { SupporterWall } from "../components/SupporterWall";
import { CampaignUpdates } from "../components/CampaignUpdates";
import { RequestExtension } from "../components/RequestExtension";
import {
  getApprovedExtensionForCampaign,
  getPendingExtensionForCampaign,
  canRequestExtension,
  subscribeExtensions,
} from "../lib/campaignExtensions";
import { getPolicy, subscribePolicy } from "../lib/platformPolicy";
import { CAMPAIGNS, IMAGES } from "../data/content";
import { useNoIndex } from "../lib/useNoIndex";

const money = (n) => `$${Number(n).toLocaleString()}`;
const supporterSeed = (n) => Math.max(6, Math.round((n || 0) / 120));
const PROOF_AVATARS = [
  { initials: "BF" },
  { initials: "KS" },
  { initials: "AO" },
  { initials: "JP" },
];
// Warm palette for feed avatars — unified to a single style for visual clarity.
const FEED_AVATAR_STYLE = "bg-bone border border-border text-ink";
const initialsOf = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const Card = ({ className = "", children, ...props }) => (
  <div className={`rounded-lg border border-border bg-bone ${className}`} {...props}>{children}</div>
);

export default function CampaignDetail() {
  useNoIndex();
  const { id } = useParams();
  const c = CAMPAIGNS[Number(id)];

  const [createOpen, setCreateOpen] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);
  // Flips true when the visitor completes a donation in this session — so the
  // Supporter Wall can unlock its composer even if the donor didn't tick
  // "remember me" (in which case no profile lands in localStorage).
  const [sessionDonated, setSessionDonated] = useState(false);

  // Live, prototype-only donation state (feed + running total).
  const [donations, setDonations] = useState(() =>
    c ? c.donations.map((d, i) => ({ ...d, _id: `seed-${i}` })) : []
  );
  const [raised, setRaised] = useState(c ? c.raised : 0);
  const prevRaisedRef = useRef(c ? c.raised : 0);
  const [supporters, setSupporters] = useState(c ? supporterSeed(c.raised) : 0);
  const prevSupportersRef = useRef(0);

  // Approved-extension state — read live so if an admin approves an
  // extension while a donor is on the page, the countdown reflects it.
  const [approvedExt, setApprovedExt] = useState(() =>
    getApprovedExtensionForCampaign(Number(id)),
  );
  const [pendingExt, setPendingExt] = useState(() =>
    getPendingExtensionForCampaign(Number(id)),
  );
  useEffect(
    () =>
      subscribeExtensions(() => {
        setApprovedExt(getApprovedExtensionForCampaign(Number(id)));
        setPendingExt(getPendingExtensionForCampaign(Number(id)));
      }),
    [id],
  );
  const extraDays = approvedExt ? approvedExt.daysAdded || 0 : 0;
  const effectiveDaysLeft = Math.max(0, (c ? c.daysLeft : 0) + extraDays);

  // Live platform policy so the timing microcopy reflows the moment
  // the founder edits campaign length or extension length in
  // /admin/settings.
  const [policy, setPolicy] = useState(getPolicy);
  useEffect(() => subscribePolicy(setPolicy), []);
  const extensionEligibility = canRequestExtension(Number(id));

  // Signal counter — bumped by the inline "Request an extension" link
  // on the urgency pill. When it increments, the RequestExtension panel
  // scrolls into view and opens its dialog automatically.
  const [openExtensionSignal, setOpenExtensionSignal] = useState(0);
  const extensionPanelRef = useRef(null);
  const requestExtensionFromPill = () => {
    if (extensionPanelRef.current) {
      extensionPanelRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    // Small delay so the smooth-scroll starts before the dialog opens —
    // avoids a jarring dialog-over-viewport-shift moment.
    setTimeout(() => setOpenExtensionSignal((n) => n + 1), 350);
  };

  useEffect(() => {
    const camp = CAMPAIGNS[Number(id)];
    setDonations(camp ? camp.donations.map((d, i) => ({ ...d, _id: `seed-${i}` })) : []);
    setRaised(camp ? camp.raised : 0);
    prevRaisedRef.current = camp ? camp.raised : 0;
    setSupporters(camp ? supporterSeed(camp.raised) : 0);
    prevSupportersRef.current = 0;
  }, [id]);

  useEffect(() => {
    prevRaisedRef.current = raised;
  }, [raised]);

  useEffect(() => {
    prevSupportersRef.current = supporters;
  }, [supporters]);

  const handleDonation = (payload) => {
    // payload: { amount, boost, boostAmount, total, showName, showAmount, displayName }
    const amt = payload && typeof payload === "object" ? payload.amount : payload;
    if (!amt) return;
    const showName = payload && typeof payload === "object" ? payload.showName !== false : true;
    const showAmount = payload && typeof payload === "object" ? payload.showAmount !== false : true;
    const displayName = (payload && payload.displayName) || (showName ? "You" : "Anonymous");
    const boosted = !!(payload && payload.boost);
    setRaised((r) => r + amt);
    setSupporters((s) => s + 1);
    setSessionDonated(true);
    setDonations((prev) => [
      {
        name: displayName,
        amount: amt,
        date: "Just now",
        hidden: !showAmount,
        boosted,
        _id: `new-${Date.now()}`,
      },
      ...prev,
    ]);
  };

  // Build media list: gallery photos + one video. Filter out any entries
  // whose image key can't be resolved (e.g. campaigns without a photo).
  const media = c
    ? [
        ...(c.gallery || [c.img])
          .map((k) => ({ type: "image", src: IMAGES[k] }))
          .filter((m) => Boolean(m.src)),
        ...(c.video ? [{ type: "video", src: c.video, poster: IMAGES[c.img] }] : []),
      ]
    : [];
  const [active, setActive] = useState(0);

  if (!c) {
    return (
      <main className="bg-sand">
        <Navbar onStart={() => setCreateOpen(true)} onDonate={() => setDonateOpen(true)} />
        <div className="mx-auto flex min-h-[60vh] max-w-3xl flex-col items-center justify-center px-6 text-center">
          <h1 className="font-serif text-3xl lg:text-[3.5rem] text-ink">Campaign not found</h1>
          <Link to="/demo#campaigns" className="mt-6 rounded-full bg-yellow px-6 py-3 text-base font-semibold text-ink">Browse students</Link>
        </div>
        <Footer onStart={() => setCreateOpen(true)} onDonate={() => setDonateOpen(true)} />
      </main>
    );
  }

  const pct = Math.round((raised / c.goal) * 100);
  const stillNeeded = Math.max(c.goal - raised, 0);
  const activeMedia = media[active];

  return (
    <main data-testid="campaign-detail-page" className="bg-sand">
      <Navbar onStart={() => setCreateOpen(true)} onDonate={() => setDonateOpen(true)} />

      <div className="mx-auto max-w-7xl px-6 pt-36 pb-24 lg:px-8 lg:pt-44">
        <FadeUp tier="sm">
          <PageAccent className="mb-5" />
        </FadeUp>
        <Link to="/demo#campaigns" data-testid="back-to-browse" className="inline-flex items-center gap-1.5 text-base font-medium text-olive transition-colors hover:text-ink">
          <ChevronLeft className="h-4 w-4" /> Back to browse students
        </Link>

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10">
          {/* Left column */}
          <div className="lg:col-span-2">
            <FadeUp>
              {/* Hero banner */}
              <div className="relative overflow-hidden rounded-lg border border-border">
                {IMAGES[c.img] ? (
                  <>
                    <img src={IMAGES[c.img]} alt={c.name} className="h-72 w-full object-cover sm:h-96" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                      <h1 className="font-serif text-3xl lg:text-[3.5rem] leading-none tracking-tight text-sand">{c.name}</h1>
                      <p className="mt-2 text-sand/85">{c.headline}</p>
                      <div className="mt-4 flex flex-wrap gap-2.5">
                        <Pill icon={Building2}>{c.school}</Pill>
                        <Pill icon={StudentIcon}>{c.major.split(",")[0]}</Pill>
                        <Pill icon={CalendarDays}>Graduating in {c.gradYear}</Pill>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="h-72 w-full sm:h-96">
                    <PortraitFallback
                      name={c.name}
                      school={c.school}
                      gradYear={c.gradYear}
                      variant={c.paletteVariant}
                      nameSize="text-[clamp(4rem,11vw,9rem)]"
                    />
                  </div>
                )}
              </div>

              {/* When no cover photo, surface the student's major + graduation
                  as a pill row directly under the fallback tile. */}
              {!IMAGES[c.img] && (
                <div className="mt-4 flex flex-wrap items-center gap-2.5">
                  <Pill icon={StudentIcon}>{c.major.split(",")[0]}</Pill>
                  <Pill icon={CalendarDays}>Graduating in {c.gradYear}</Pill>
                  <p className="ml-1 text-[13px] italic text-olive">{c.headline}</p>
                </div>
              )}
            </FadeUp>

            <FadeUp>
              <h2 className="mt-10 font-serif text-3xl text-ink">{c.name.split(" ")[0]}&rsquo;s Campaign</h2>
            </FadeUp>

            {/* My Story */}
            <FadeUp>
              <Card className="mt-5 p-7 lg:p-8">
                <h3 className="font-serif text-3xl text-ink">My story</h3>
                <p className="mt-4 text-base leading-relaxed text-olive">{c.story}</p>
              </Card>
            </FadeUp>

            {/* Media */}
            <FadeUp>
              <Card className="mt-6 p-7 lg:p-8" data-testid="campaign-media">
                <h3 className="font-serif text-3xl text-ink">Media</h3>
                <div className="mt-5 overflow-hidden rounded-md border border-border bg-secondary">
                  {activeMedia?.type === "video" ? (
                    <video key={activeMedia.src} data-testid="media-video" controls poster={activeMedia.poster} className="aspect-video w-full bg-ink object-cover">
                      <source src={activeMedia.src} type="video/mp4" />
                    </video>
                  ) : (
                    <img src={activeMedia?.src} alt="Campaign media" className="aspect-video w-full object-cover" />
                  )}
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  {media.map((m, i) => (
                    <button
                      key={i}
                      data-testid={`media-thumb-${i}`}
                      onClick={() => setActive(i)}
                      className={`relative h-16 w-24 shrink-0 overflow-hidden rounded-md border-2 transition-colors ${active === i ? "border-tangerine" : "border-transparent hover:border-border"}`}
                    >
                      <img src={m.type === "video" ? m.poster : m.src} alt="" className="h-full w-full object-cover" />
                      {m.type === "video" && (
                        <span className="absolute inset-0 flex items-center justify-center bg-ink/40">
                          <Play className="h-5 w-5 fill-sand text-sand" />
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </Card>
            </FadeUp>
          </div>

          {/* Right column */}
          <div className="lg:col-span-1">
            <div className="space-y-6 lg:sticky lg:top-28">
              {/* Progress */}
              <FadeUp>
                <Card className="p-6 lg:p-7">
                  {effectiveDaysLeft <= 14 && (
                    <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                      <motion.div
                        data-testid="urgency-pill"
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="inline-flex items-center gap-2 rounded-full bg-scarlet/10 px-3.5 py-1.5 text-[13px] font-semibold text-scarlet"
                      >
                        <motion.span
                          className="flex h-2 w-2 rounded-full bg-scarlet"
                          animate={{ scale: [1, 1.4, 1], opacity: [1, 0.5, 1] }}
                          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                        />
                        Only {effectiveDaysLeft} {effectiveDaysLeft === 1 ? "day" : "days"} left
                      </motion.div>
                      {/* Subtle owner-only affordance — pulls the extension
                          request forward to the exact moment the countdown
                          becomes visible. In production this is gated by
                          `session.userId === campaign.ownerId`; today the
                          prototype shows it to everyone (matching the
                          existing RequestExtension panel behaviour). */}
                      {extensionEligibility.canRequest && (
                        <button
                          type="button"
                          onClick={requestExtensionFromPill}
                          data-testid="urgency-extension-link"
                          className="text-[13px] font-medium text-ink/70 underline underline-offset-[3px] decoration-ink/30 transition hover:text-ink hover:decoration-ink"
                        >
                          Request {policy.extensionDays} more days
                        </button>
                      )}
                      {pendingExt && (
                        <span
                          data-testid="urgency-extension-pending"
                          className="text-[13px] italic text-cobalt"
                        >
                          Extension request under review
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="font-serif text-3xl leading-none text-ink">
                        <CountUp key={raised} startOnMount from={prevRaisedRef.current} to={raised} prefix="$" duration={1.2} />
                      </p>
                      <p className="mt-1 text-[13px] uppercase tracking-[0.15em] text-olive">Raised</p>
                    </div>
                    <div className="text-right">
                      <p className="font-serif text-3xl leading-none text-ink">{money(c.goal)}</p>
                      <p className="mt-1 text-[13px] uppercase tracking-[0.15em] text-olive">Goal</p>
                    </div>
                  </div>
                  <div className="mt-5 h-3 w-full overflow-hidden rounded-full bg-secondary">
                    <motion.div
                      className="h-full rounded-full bg-yellow"
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(pct, 100)}%` }}
                      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    />
                  </div>
                  <div className="mt-5 flex items-center justify-between rounded-md bg-secondary px-4 py-3">
                    <span className="text-[13px] font-medium text-olive">Still needed</span>
                    <span className="font-serif text-3xl text-ink">{money(stillNeeded)}</span>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <Stat icon={TrendingUp} label="Progress" value={`${pct}%`} />
                    <Stat icon={CalendarDays} label="Days left" value={`${effectiveDaysLeft} ${effectiveDaysLeft === 1 ? "day" : "days"}`} />
                  </div>
                  <p
                    data-testid="campaign-timing-note"
                    className="mt-3 text-[13px] leading-snug text-olive"
                  >
                    Every campaign is live for {policy.campaignDurationDays} days, with one {policy.extensionDays}-day extension available on request. When the clock runs out, so does the ask.
                  </p>
                  {approvedExt && (
                    <p
                      data-testid="extension-approved-note"
                      className="mt-3 flex items-center gap-1.5 text-[13px] italic text-grass"
                    >
                      <CalendarDays className="h-3 w-3" strokeWidth={2} />
                      Extension approved — {extraDays} additional days added by My Rich Aunt.
                    </p>
                  )}

                  <div className="mt-5 flex items-center gap-3" data-testid="supporter-proof">
                    <div className="flex -space-x-2.5">
                      {PROOF_AVATARS.map((a, i) => (
                        <motion.span
                          key={a.initials}
                          initial={{ opacity: 0, scale: 0.4, x: -10 }}
                          animate={{ opacity: 1, scale: 1, x: 0 }}
                          transition={{ delay: 0.25 + i * 0.06, duration: 0.3, ease: [0.22, 0.61, 0.36, 1] }}
                          className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-bone bg-sand text-[13px] font-bold text-ink ring-1 ring-border"
                        >
                          {a.initials}
                        </motion.span>
                      ))}
                      <motion.span
                        initial={{ opacity: 0, scale: 0.4 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.25 + PROOF_AVATARS.length * 0.06, duration: 0.3, ease: [0.22, 0.61, 0.36, 1] }}
                        className="flex h-8 min-w-8 items-center justify-center rounded-full border-2 border-bone bg-ink px-1.5 text-[13px] font-bold text-bone"
                      >
                        +{Math.max(supporters - PROOF_AVATARS.length, 0)}
                      </motion.span>
                    </div>
                    <p className="text-[13px] text-olive">
                      <CountUp key={supporters} startOnMount from={prevSupportersRef.current} to={supporters} duration={1} className="font-semibold text-ink" />
                      {" "}donors this week
                    </p>
                  </div>

                  <button
                    data-testid="detail-donate-button"
                    onClick={() => setDonateOpen(true)}
                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-ink px-6 py-4 text-base font-semibold text-sand transition-colors hover:bg-forest"
                  >
                    <HeartIcon className="h-4 w-4" /> Donate now
                  </button>

                  <CampaignShareBar campaign={c} raised={raised} />
                </Card>
              </FadeUp>

              {/* Donation feed — warm, celebratory */}
              <FadeUp>
                <Card className="relative overflow-hidden p-6 lg:p-7" data-testid="donation-feed">
                  <span className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-yellow/25 blur-3xl" />
                  <span className="pointer-events-none absolute -left-14 -bottom-14 h-36 w-36 rounded-full bg-blossom/25 blur-3xl" />
                  <div className="relative flex items-center gap-2.5">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-tangerine/12">
                      <HeartIcon className="h-4.5 w-4.5 text-tangerine" />
                    </span>
                    <div>
                      <h3 className="font-serif text-3xl leading-none text-ink">Donation feed</h3>
                      <p className="text-[13px] text-olive">A warm welcome to every supporter.</p>
                    </div>
                  </div>
                  <ul className="relative mt-5 space-y-2">
                    <AnimatePresence initial={false}>
                      {donations.map((d, idx) => {
                        const isNew = typeof d._id === "string" && d._id.startsWith("new-");
                        const isAnon = d.name === "Anonymous";
                        return (
                          <motion.li
                            key={d._id}
                            layout
                            data-testid="donation-row"
                            initial={{
                              opacity: 0,
                              y: -6,
                              backgroundColor: isNew ? "rgba(253,208,6,0.35)" : "rgba(253,208,6,0)",
                            }}
                            animate={{
                              opacity: 1,
                              y: 0,
                              backgroundColor: "rgba(253,208,6,0)",
                            }}
                            transition={{
                              duration: 0.5,
                              ease: [0.22, 1, 0.36, 1],
                              backgroundColor: { duration: 2.2 },
                            }}
                            className="flex items-center gap-3 rounded-lg border border-border/60 bg-bone px-3.5 py-3"
                          >
                            <span
                              className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[13px] font-bold ${ isAnon ? "bg-secondary text-olive" : FEED_AVATAR_STYLE }`}
                            >
                              {isAnon ? <User className="h-4 w-4" strokeWidth={2} /> : initialsOf(d.name)}
                              {isNew && (
                                <motion.span
                                  aria-hidden
                                  className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-tangerine text-bone"
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ duration: 0.3, ease: [0.22, 0.61, 0.36, 1], delay: 0.15 }}
                                >
                                  <HeartIcon className="h-2.5 w-2.5" />
                                </motion.span>
                              )}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="truncate text-[13px] font-semibold text-ink">{d.name}</span>
                                {d.boosted && (
                                  <span
                                    data-testid="boost-chip"
                                    title="Included 5% overhead boost"
                                    className="inline-flex items-center gap-1 rounded-full bg-tangerine/12 px-2 py-0.5 text-[13px] font-semibold uppercase tracking-wide text-tangerine"
                                  >
                                    <HeartIcon className="h-3 w-3" /> +5%
                                  </span>
                                )}
                              </div>
                              <p className="mt-0.5 text-[13px] text-olive">{d.date}</p>
                            </div>
                            {d.hidden ? (
                              <span
                                data-testid="donation-amount-hidden"
                                title="Donor chose to keep the amount private"
                                className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-[13px] font-medium text-olive"
                              >
                                <EyeOff className="h-3 w-3" strokeWidth={2} /> Private
                              </span>
                            ) : (
                              <span
                                data-testid="donation-amount"
                                className={`rounded-full px-3 py-1 text-[13px] font-bold ${ isNew ? "bg-yellow text-ink" : "bg-yellow/25 text-ink" }`}
                              >
                                {money(d.amount)}
                              </span>
                            )}
                          </motion.li>
                        );
                      })}
                    </AnimatePresence>
                  </ul>
                </Card>
              </FadeUp>

              {/* Campaign updates — student & admin posts about progress */}
              <FadeUp>
                <div data-testid="campaign-updates-section">
                  <div className="mb-4 flex items-baseline justify-between">
                    <h3 className="font-serif text-3xl leading-none text-ink">Updates from {c.name.split(" ")[0]}</h3>
                    <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">
                      Progress notes
                    </span>
                  </div>
                  <CampaignUpdates
                    campaignId={Number(id)}
                    studentFirstName={c.name.split(" ")[0]}
                  />
                </div>
              </FadeUp>

              {/* Supporter Wall — encouraging messages from donors + supporters */}
              <FadeUp>
                <SupporterWall
                  campaignId={Number(id)}
                  studentFirstName={c.name.split(" ")[0]}
                  hasDonatedThisSession={sessionDonated}
                  onOpenDonate={() => setDonateOpen(true)}
                />
              </FadeUp>

              {/* Student-owner panel: extension requests (mocked auth for the prototype) */}
              <FadeUp>
                <div ref={extensionPanelRef}>
                  <RequestExtension
                    campaignId={Number(id)}
                    studentFirstName={c.name.split(" ")[0]}
                    goalAmount={c.goal}
                    signalOpenRequest={openExtensionSignal}
                  />
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </div>


      <Footer onStart={() => setCreateOpen(true)} onDonate={() => setDonateOpen(true)} />
      <CreateCampaign open={createOpen} onOpenChange={setCreateOpen} />
      <DonateDialog open={donateOpen} onOpenChange={setDonateOpen} raised={raised} goal={c.goal} studentName={c.name.split(" ")[0]} campaignImage={IMAGES[c.img]} campaignDaysLeft={c.daysLeft} onComplete={handleDonation} />
    </main>
  );
}

const Pill = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-sand/95 px-3 py-1.5 text-[13px] font-semibold text-ink">
    <Icon className="h-3.5 w-3.5 text-tangerine" strokeWidth={2} /> {children}
  </span>
);

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-md border border-border bg-secondary/50 p-4">
    <span className="flex items-center gap-1.5 text-[13px] font-medium text-olive"><Icon className="h-3.5 w-3.5" strokeWidth={2} /> {label}</span>
    <p className="mt-2 font-serif text-3xl text-ink">{value}</p>
  </div>
);
